/**
 * IvyDockContext — dock open/closed channel (Phase 2D).
 *
 * The IvyDock is portal-mounted at App root below <Routes>, so it persists
 * across all route changes. Consumers (e.g. Sidebar's Ivy button, IvyPage's
 * auto-open on mount) call `toggleDock()` / `openDock()` / `closeDock()` to
 * drive the dock's expanded-vs-collapsed state without reaching through
 * prop-drilling or DOM events.
 *
 * Semantics:
 *   - `isOpen = true`  → dock is expanded (full chat panel visible)
 *   - `isOpen = false` → dock is collapsed (breathing orb only — the
 *                         Phase 2A GraspPodIvy FAB visual)
 *
 * The dock is ALWAYS mounted (it lives at App root); `isOpen` only controls
 * the visual expansion state. Collapsed persists across reloads via
 * localStorage (key: gp_ivy_dock_collapsed); see IvyDock.tsx for persistence.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

interface IvyDockContextValue {
  isOpen: boolean
  toggleDock: () => void
  openDock: () => void
  closeDock: () => void
}

const IvyDockContext = createContext<IvyDockContextValue | null>(null)

const LS_KEY = 'gp_ivy_dock_collapsed'

/** Read initial collapsed state from localStorage. Default: collapsed (false isOpen).
 *  Matches the pre-2D behavior of the FAB starting closed. */
function readInitialIsOpen(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const raw = window.localStorage.getItem(LS_KEY)
    if (raw === null) return false
    // localStorage stores `collapsed` (inverse of isOpen). Parse defensively.
    const collapsed = raw === 'true'
    return !collapsed
  } catch {
    return false
  }
}

/** Persist the inverse (`collapsed`) so the LS semantics match the scope-lock key name. */
function persistIsOpen(isOpen: boolean): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LS_KEY, String(!isOpen))
  } catch {
    // Quota exceeded / storage disabled — silent, not worth breaking the UI
  }
}

export function IvyDockProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState<boolean>(readInitialIsOpen)

  const openDock = useCallback(() => {
    setIsOpen(true)
    persistIsOpen(true)
  }, [])

  const closeDock = useCallback(() => {
    setIsOpen(false)
    persistIsOpen(false)
  }, [])

  const toggleDock = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev
      persistIsOpen(next)
      return next
    })
  }, [])

  const value = useMemo<IvyDockContextValue>(
    () => ({ isOpen, toggleDock, openDock, closeDock }),
    [isOpen, toggleDock, openDock, closeDock],
  )

  return <IvyDockContext.Provider value={value}>{children}</IvyDockContext.Provider>
}

export function useIvyDock(): IvyDockContextValue {
  const ctx = useContext(IvyDockContext)
  if (!ctx) {
    throw new Error('useIvyDock must be called within IvyDockProvider')
  }
  return ctx
}
